import { Navigate } from "react-router";
import { useAuth } from "../../store/useAuth";
import { SubmitHandler, useForm } from "react-hook-form";
import { GoogleAuthProvider, getAuth, signInWithPopup } from "firebase/auth";
import { ReactComponent as GoogleIcon } from "../../assets/icons/auth/google-icon.svg";
import { useState } from "react";
import { LOG } from "../../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "../../store/useAppLoading";
import { useAlert } from "../../store/useAlert";
import { getErrorMessage } from "../../utils/helpers";

const provider = new GoogleAuthProvider();

type Inputs = {
  email: string;
  password: string;
};

export default function LoginScreen() {
  /** Hooks */
  const { isAuthenticated, setLoginData, user } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const { setIsLoading } = useAppLoading();
  const { setAlert } = useAlert();
  /** State */
  const [googleLoading, setGoogleLoading] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    try {
      const response = await axios.post("login", data);
      if (response?.data?.user?.user_role === 3) {
        setAlert({
          isOpen: true,
          message: "You are not authorized to access this page",
          severity: "error",
        });
        return;
      }
      if (response?.data?.user?.pending_delete) {
        setAlert({
          isOpen: true,
          message: "Your account has been deactivated",
          severity: "error",
        });
        return;
      }
      if (response?.data?.accessToken) {
        setLoginData(
          response?.data?.user,
          response?.data?.accessToken,
          response?.data?.refreshToken
        );
        return;
      }
      setAlert({
        isOpen: true,
        message: "Login failed. Please try again.",
        severity: "error",
      });
    } catch (error) {
      LOG.error("Login", { error });
      setAlert({
        isOpen: true,
        message: getErrorMessage(error, "Login failed. Please try again."),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setGoogleLoading(true);
    try {
      const auth = getAuth();
      const userInfo = await signInWithPopup(auth, provider);
      LOG.info("User has signed in succesfully with Google");
      // The backend checks this token and reads who signed in from it; the
      // body carries nothing else that identifies the person.
      const idToken = await userInfo.user.getIdToken();
      const socialLoginBody = {
        idToken,
        first_name: userInfo?.user?.displayName?.split(" ")?.[0] ?? "",
        last_name: userInfo?.user?.displayName?.split(" ")?.[1] ?? "",
      };
      const response = await axios.post("social", socialLoginBody);
      if (response?.data?.user?.user_role === 3) {
        setAlert({
          isOpen: true,
          message: "You are not authorized to access this page",
          severity: "error",
        });
        return;
      }
      if (response?.data?.user?.pending_delete) {
        setAlert({
          isOpen: true,
          message: "Your account has been deactivated",
          severity: "error",
        });
        return;
      }
      if (response?.data?.accessToken) {
        setLoginData(
          response?.data?.user,
          response?.data?.accessToken,
          response?.data?.refreshToken
        );
        return;
      }
      setAlert({
        isOpen: true,
        message: "Google sign-in failed. Please try again.",
        severity: "error",
      });
    } catch (error: any) {
      LOG.error("SignIn with Google", { error });
      const isUserCancelled =
        error?.code === "auth/popup-closed-by-user" ||
        error?.code === "auth/cancelled-popup-request";
      if (isUserCancelled) {
        return;
      }
      setAlert({
        isOpen: true,
        // The axios interceptor rejects with the response, so the body is on error.data.
        message:
          (error?.data?.errorCode ?? error?.response?.data?.errorCode) === "NO_ACCOUNT"
            ? "This Google account is not linked to a Droppie account. Sign in with your email and password."
            : getErrorMessage(error, "Google sign-in failed. Please try again."),
        severity: "error",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  if (isAuthenticated) {
    if (user?.user_role === 6) {
      return <Navigate to="/plastic-dashboard/conversion" replace />;
    }
    return <Navigate to="/" />;
  }

  return (
    <div className="w-full h-screen flex items-center justify-center flex-col">
      <h1 className="font-default text-6xl font-bold mb-14 text-dark">
        Droppie
      </h1>
      <div className="bg-white px-6 py-10 rounded-xl border-gray-border drop-shadow-none w-[90%] lg:w-1/3">
       <button
          onClick={signInWithGoogle}
          disabled={googleLoading}
          className={`outline-none w-full h-12 bg-white active:bg-gray-50 border border-gray-border rounded transition ease-linear mb-3 flex flex-row items-center justify-center ${
            googleLoading ? "bg-gray-50" : "bg-white"
          }`}
        >
          <GoogleIcon />
          <span className="font-satoshi text-base font-medium text-dark ml-4">
            Sign in with Google
          </span>
        </button>
        <div className="flex flex-row items-center w-full mb-3">
          <div className="w-1/2 h-[1px] bg-gray-border" />
          <span className="font-satoshi text-sm font-normal text-dark mx-2">
            or
          </span>
          <div className="w-1/2 h-[1px] bg-gray-border" />
        </div> 
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="font-satoshi text-sm font-medium text-tailgrids-black mb-3">
            Email
          </div>
          <input
            {...register("email", { required: true, pattern: /^\S+@\S+$/i })}
            type="email"
            placeholder="email"
            className={`border ${
              errors.email
                ? "border-red-base focus:outline-red-base"
                : "border-gray-border focus:outline-focused-border"
            } rounded h-12 w-full px-5 py-3 font-satoshi placeholder:text-input-placeholder mb-6`}
          />
          <div className="font-satoshi text-sm font-medium text-tailgrids-black mb-3">
            Password
          </div>
          <input
            {...register("password", { required: true })}
            type="password"
            placeholder="password"
            className={`border ${
              errors.password
                ? "border-red-base focus:outline-red-base"
                : "border-gray-border focus:outline-focused-border"
            } rounded h-12 w-full focus:outline-focused-border px-5 py-3 font-satoshi placeholder:text-input-placeholder mb-6`}
          />
          <button
            type="submit"
            className="outline-none w-full h-12 bg-primary-base active:bg-primary-darkest rounded transition ease-linear mb-3"
          >
            <span className="font-satoshi text-base font-medium text-white">
              Login
            </span>
          </button>
        </form>
        <button className="outline-none w-full h-12 active:bg-primary-lightest rounded transition ease-linear">
          <span className="font-satoshi text-base font-bold text-primary-base focus:text-primary-darkest">
            Forgot password
          </span>
        </button>
      </div>
    </div>
  );
}
